import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
  changeDirectSalesData,
  updateBackupFormDataLine,
  updateDirectSalesSessionLock,
  updateTransientLineStates,
} from 'store/dealManagement/reducer';
import { getDirectSalesCampaign } from 'modules/api/directSalesCampaign';
import { cancelFunctions } from 'utils/cancelFunctions';
import { Store } from 'components/common/types/Store.types';
import { changeSelectedProducts } from 'store/productManagement/reducer';
import {
  DirectSalesLine,
  TransformedDirectSalesBookingResponse,
} from 'components/common/types/DirectSalesCampaign.types';
import { DirectSalesCampaignResponse } from 'components/common/types/DirectSalesCampaignResponse.types';
import { addToastNotification } from 'store/notification/reducer';
import { ProductTemplate } from 'components/common/types/DirectSalesProductTemplate.types';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import { getCurrentLineId } from 'store/dealManagement/selectors/getCurrentLineId';
import { getMarketId } from 'store/publisher/selectors';
import { transformTargetStatusesToTransientDealLinesState } from '../utils/transformTargetStatusesToTransientDealLinesState';
import { isDirectSalesCampaignCloned } from '../utils/isDirectSalesCampaignCloned';
import { transformDirectSalesBookingResponse } from '../utils/transformDirectSalesCampaignResponses';
import { getAllDealLineProductTemplates } from '../utils/getAllDealLineProductTemplates';

export type GetUpdatedDirectSalesCampaignResult = Promise<
  DirectSalesCampaignResponse & TransformedDirectSalesBookingResponse
>;
type UseRetrieveDirectSalesCampaign = (
  customCampaignId?: string,
  shouldSyncProductTemplates?: boolean,
) => GetUpdatedDirectSalesCampaignResult;

const useRetrieveDirectSalesCampaign = (): UseRetrieveDirectSalesCampaign => {
  const dispatch = useDispatch();
  const campaignId = useSelector(getDealId);
  const currentLineId = useSelector(getCurrentLineId);
  const marketId = useSelector(getMarketId);
  const selectedProducts = useSelector((state: Store) => state.productManagement.selectedProducts);

  const errorCallback = (): void => {
    dispatch(
      addToastNotification({
        description: 'There was a problem loading the product template. Refresh the page and try again.',
        type: 'Failure',
      }),
    );
  };

  const updateSelectedProductTemplates = async (lines: Partial<DirectSalesLine>[]): Promise<void> => {
    const linesToFetch = lines.filter((line) => {
      if (!line.productTemplate?.id || line.productLineReference === undefined) {
        return false;
      }

      const alreadyLoaded = selectedProducts.some(
        (product: ProductTemplate) =>
          product.dealLineId === line.lineId &&
          product.productTemplate?.id === line.productTemplate?.id &&
          product.productLineReference === line.productLineReference,
      );

      return !alreadyLoaded;
    });

    if (linesToFetch.length === 0) {
      return;
    }

    const filteredEmptyProductTemplates = await getAllDealLineProductTemplates(
      linesToFetch,
      marketId,
      cancelFunctions,
      errorCallback,
    );

    if (filteredEmptyProductTemplates.length) {
      dispatch(changeSelectedProducts([...selectedProducts, ...filteredEmptyProductTemplates]));
    }
  };

  const syncSelectedProductTemplates = async (lines: Partial<DirectSalesLine>[]): Promise<void> => {
    const linesWithValidTemplates = lines.filter(
      (line) => line.productTemplate?.id && line.productLineReference !== undefined,
    );

    if (linesWithValidTemplates.length === 0) {
      dispatch(changeSelectedProducts([]));
      return;
    }

    const newProductTemplates = await getAllDealLineProductTemplates(
      linesWithValidTemplates,
      marketId,
      cancelFunctions,
      errorCallback,
    );

    dispatch(changeSelectedProducts(newProductTemplates));
  };

  return async (customCampaignId: string = campaignId, shouldSyncProductTemplates = false) => {
    const result = await getDirectSalesCampaign(customCampaignId, marketId, cancelFunctions);
    const transformResult = transformDirectSalesBookingResponse(result);

    const hasProductTemplatesApplied = transformResult.lines.some(({ productTemplate }) => !!productTemplate?.id);

    if (shouldSyncProductTemplates) {
      await syncSelectedProductTemplates(transformResult.lines);
    } else if (hasProductTemplatesApplied) {
      await updateSelectedProductTemplates(transformResult.lines);
    }

    if (isDirectSalesCampaignCloned(result.id) && result.campaign.body.campaignDefinition.dealLines.length >= 1) {
      const targetStatuses = transformTargetStatusesToTransientDealLinesState(
        result.campaign.body.campaignDefinition.dealLines,
      );

      if (!isEmpty(targetStatuses)) {
        dispatch(updateTransientLineStates(targetStatuses));
      }
    }

    dispatch(updateDirectSalesSessionLock(transformResult.sessionLockState));

    transformResult.lines.forEach(({ lineId, allocationFailures }) => {
      if (!lineId) return;

      const nextAllocationFailures = allocationFailures ?? [];

      dispatch(
        updateBackupFormDataLine({
          lineId,
          newBackupLineData: { allocationFailures: nextAllocationFailures },
        }),
      );

      if (lineId === currentLineId) {
        dispatch(changeDirectSalesData({ allocationFailures: nextAllocationFailures }));
      }
    });

    return {
      ...result,
      ...transformResult,
    };
  };
};

export default useRetrieveDirectSalesCampaign;
